import { useState, useRef, useEffect } from 'react'
import type { FC, ReactNode, TouchEvent } from 'react'

import { Spin } from '@/components'
import { SC_PullContainer, SC_PullIndicator } from './styled'


interface PullToRefreshProps {
  children: ReactNode
  onRefresh: () => Promise<void> | void
  disabled?: boolean
}

export const PullToRefresh: FC<PullToRefreshProps> = ({
  children,
  onRefresh,
  disabled = false,
}) => {
  const [ pullDistance, setPullDistance ] = useState(0)
  const [ isRefreshing, setIsRefreshing ] = useState(false)

  const startY = useRef<number>(0)
  const currentY = useRef<number>(0)
  const isPulling = useRef(false)
  const startAtTop = useRef(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (disabled) {
      setPullDistance(0)
      isPulling.current = false
    }
  }, [ disabled ])

  const MAX_PULL_DISTANCE = 120
  const REFRESH_THRESHOLD = 70

  const getScrollParent = (
    node: HTMLElement | null
  ): HTMLElement | Window => {
    if (!node) return window

    if (node.scrollHeight > node.clientHeight) {
      const overflowY = window.getComputedStyle(node).overflowY
      if (overflowY === 'scroll' || overflowY === 'auto') {
        return node
      }
    }

    return getScrollParent(node.parentElement)
  }

  const getScrollTop = () => {
    if (!containerRef.current) return 0
    const scrollParent = getScrollParent(containerRef.current)
    if (scrollParent === window) return window.scrollY
    return (scrollParent as HTMLElement).scrollTop
  }

  const handleTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    if (disabled || isRefreshing) return

    startY.current = e.touches[0].clientY
    startAtTop.current = getScrollTop() <= 0
    isPulling.current = false
  }

  const handleTouchMove = (e: TouchEvent<HTMLDivElement>) => {
    if (disabled || isRefreshing || !startAtTop.current) return

    const scrollTop = getScrollTop()
    const y = e.touches[0].clientY
    const deltaY = y - startY.current

    // Check if we are at the top and pulling down
    if (scrollTop <= 0 && deltaY > 0) {
      // If we weren't pulling yet, this is the start
      if (!isPulling.current) {
        isPulling.current = true
      }

      // Prevent default to stop native scrolling/overscroll
      if (e.cancelable) {
        e.preventDefault()
      }

      // Add resistance
      const resistance = 0.5
      const newDistance = Math.min(deltaY * resistance, MAX_PULL_DISTANCE)
      setPullDistance(newDistance)
      currentY.current = y
    } else {
      isPulling.current = false
      setPullDistance(0)
    }
  }

  const handleTouchEnd = async () => {
    if (disabled || isRefreshing) return

    if (isPulling.current && pullDistance > REFRESH_THRESHOLD) {
      setIsRefreshing(true)
      setPullDistance(REFRESH_THRESHOLD) // Snap to threshold

      try {
        await onRefresh()
      } finally {
        // We might not reach here if page reloads, but good practice
        setIsRefreshing(false)
        setPullDistance(0)
      }
    } else {
      setPullDistance(0)
    }

    isPulling.current = false
  }


  return (
    <SC_PullContainer
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        transform: `translateY(${pullDistance}px)`,
        transition: isPulling.current ? 'none' : 'transform 0.2s cubic-bezier(0, 0, 0.31, 1)'
      }}
    >
      <SC_PullIndicator $height={REFRESH_THRESHOLD}>
        {isRefreshing || pullDistance > 20 ? (
          <Spin size='default' />
        ) : null}
      </SC_PullIndicator>

      {children}
    </SC_PullContainer>
  )
}
